import {TechnicalIssueStatus} from '@services/TechnicalSupport/types';
import {ItemTitle} from './ItemTitle';
import {FormControlLabel, Radio, RadioGroup, Stack} from '@mui/material';
import {useEnumDictionary} from 'utils/hooks/useEnumDictionary';
import {DeletePopover} from '@ui/DeletePopover/DeletePopover';
import {useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {TechnicalSupportService} from '@services/TechnicalSupport/TechnicalSupportService';
import {checkhStatusDisabled} from './utils';
import {useTranslation} from 'react-i18next';

export type TechnicalSupportIssueStatusProps = {
    currentStatus: TechnicalIssueStatus;
    afterSave: () => void;
    id: number;
};

export const TechnicalSupportIssueStatus = ({
    currentStatus,
    afterSave,
    id,
}: TechnicalSupportIssueStatusProps) => {
    const {t} = useTranslation(['administration', 'common']);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const [newValue, setNewValue] = useState<TechnicalIssueStatus | null>(null);

    const {getOptionNameByCode: getOptionNameByCodeStatus} = useEnumDictionary({
        code: 'SupportTicketStatus',
    });

    const onChangeStatusClick = (event: React.ChangeEvent<HTMLElement>, value: string) => {
        setAnchorEl(event.currentTarget);
        setNewValue(value as TechnicalIssueStatus);
    };

    const onClose = () => {
        setAnchorEl(null);
        setNewValue(null);
    };

    const mutateChangeTechnicalIssueStatus = useMutation({
        mutationKey: ['changeTechnicalIssueStatus'],
        mutationFn: (newStatus: TechnicalIssueStatus) =>
            TechnicalSupportService.changeTechnicalIssueStatus(id, newStatus),
        onSuccess: () => {
            onClose();
            afterSave();
        },
        onError: () => {
            onClose();
        },
    });

    const onSave = () => {
        mutateChangeTechnicalIssueStatus.mutate(newValue!);
    };

    return (
        <>
            <Stack>
                <ItemTitle title={t('status', {ns: 'common'})} />
                <RadioGroup
                    value={currentStatus}
                    onChange={onChangeStatusClick}
                    row
                >
                    {Object.values(TechnicalIssueStatus).map((status) => (
                        <FormControlLabel
                            key={status}
                            disabled={checkhStatusDisabled(status, currentStatus)}
                            value={status}
                            control={<Radio />}
                            label={getOptionNameByCodeStatus(status)}
                        />
                    ))}
                </RadioGroup>
            </Stack>
            {anchorEl && (
                <DeletePopover
                    onConfirmation={onSave}
                    onClose={onClose}
                    anchorEl={anchorEl}
                    title={t('technicalSupport.sureChangeStatus')}
                    description={t('technicalSupport.cannotRestorePrevStatus')}
                    confirmText={t('change')}
                />
            )}
        </>
    );
};
